import React, { useEffect, useState } from 'react';
import { Box, Stack, ButtonBase, Typography, IconButton } from '@mui/material';
import { X } from 'lucide-react';

type TabsProp = {
  tabTitles: string[];
  setTabTitles?: (titles: string[]) => void | undefined;
  activeTab: number;
  setActiveTab: (value: number) => void;
  addClose?: boolean;
  onClose?: (index: number) => void;
  disableInactiveTabs?: boolean;
  className?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  inactiveTabClassName?: string;
};

export const Tabs: React.FC<TabsProp> = ({
  tabTitles,
  setTabTitles,
  activeTab,
  setActiveTab,
  addClose = false,
  onClose,
  disableInactiveTabs = false,
  className,
  tabClassName,
  activeTabClassName,
  inactiveTabClassName,
}) => {
  const [visitedTabs, setVisitedTabs] = useState<number[]>([activeTab]);

  useEffect(() => {
    if (!visitedTabs.includes(activeTab)) {
      setVisitedTabs((prev) => [...prev, activeTab]);
    }
  }, [activeTab, visitedTabs]);

  const handleRemoveTab = (index: number) => {
    if (onClose) {
      onClose(index);
    } else if (setTabTitles !== undefined) {
      const newTabs = tabTitles.filter((_, i) => i !== index);
      setTabTitles(newTabs);
    }
  };

  return (
    <Box
      className={className}
      sx={{
        width: { xs: '100%', sm: 'auto' },
        overflowX: 'auto',
        p: 0.5,
        bgcolor: 'action.hover',
        backdropFilter: 'blur(4px)',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        display: { xs: 'block', sm: 'inline-flex' },
      }}
    >
      <Stack direction="row" spacing={0.5} sx={{ minWidth: 'max-content' }}>
        {tabTitles.map((item: string, index: number) => {
          const isActive = activeTab === index;
          const isDisabled =
            disableInactiveTabs && !visitedTabs.includes(index);

          return (
            <ButtonBase
              key={item + index}
              className={`${tabClassName || ''} ${
                isActive ? activeTabClassName || '' : inactiveTabClassName || ''
              }`}
              onClick={() => !isDisabled && setActiveTab(index)}
              disabled={isDisabled}
              sx={{
                minHeight: { xs: 36, sm: 30 },
                whiteSpace: 'nowrap',
                px: { xs: 1.5, sm: 1.25 },
                py: { xs: 0.75, sm: 0.5 },
                borderRadius: 2,
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: isActive ? 'background.paper' : 'transparent',
                color: isActive ? 'success.main' : 'text.secondary',
                boxShadow: isActive ? 1 : 0,
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  bgcolor: isActive ? 'background.paper' : 'action.selected',
                  color: isActive ? 'success.main' : 'text.primary',
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                  color: 'inherit',
                }}
              >
                {item}
              </Typography>
              {addClose && setTabTitles && index > 1 && item !== 'CCA Results' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTab(index);
                  }}
                  sx={{
                    p: 0.25,
                    color: 'text.disabled',
                    '&:hover': { color: 'error.main' },
                  }}
                  aria-label="Close Tab"
                >
                  <X size={12} />
                </IconButton>
              )}
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
};
